import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import { NavLink } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChurchIcon from '@mui/icons-material/Church';
import CampaignIcon from '@mui/icons-material/Campaign';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faXTwitter, faYoutube, faVimeoV } from '@fortawesome/free-brands-svg-icons';
import { WEEKLY_SCHEDULE } from '../../helpers/upcomingServices';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectLanguage, updateLanguage } from '../../app/slices/languageSlice';
import {
  ADDRESS_MAPS_URL,
  MIDWEEK_SHORT_NAMES,
  Eyebrow,
  SectionHeading,
  IconRow,
  LanguageSwitcher,
  MenuFooterBar,
} from '../menuShared';
import {
  EN,
  RO,
  street,
  city,
  state,
  zip,
  weekdayMap,
  getDirectionsString,
  viewFullScheduleString,
  morningServiceString,
  eveningServiceString,
  secondaryVisitEyebrowString,
  secondaryVisitHeadingString,
  secondaryGatheringsEyebrowString,
  secondaryGatheringsHeadingString,
  secondaryExploreEyebrowString,
  secondaryExploreHeadingString,
  secondaryConnectEyebrowString,
  secondaryConnectHeadingString,
  announcementsString,
  announcementsPath,
  bibleReadingPlanString,
  bibleReadingPlanPath,
  mediaString,
  mediaPath,
  ministriesString,
  awanaPath,
  ezraPath,
  serviceTimesPath,
  eventsPath,
  eventsPageHeadingString,
  facebookPath,
  instagramPath,
  twitterPath,
  youtubePath,
  vimeoPath,
} from '../../constants';

const FooterLink = ({ theme, children, ...navLinkProps }) => (
  <NavLink
    {...navLinkProps}
    style={{
      fontFamily: theme.typography.fontFamily,
      fontSize: '14px',
      fontWeight: 600,
      color: theme.palette.primary.main,
      textDecoration: 'none',
    }}
  >
    {children} →
  </NavLink>
);

export function SecondaryMenu(props) {
  const theme = useTheme();
  const language = useAppSelector(selectLanguage);
  const dispatch = useAppDispatch();
  const [ministriesOpen, setMinistriesOpen] = useState(false);

  if (!props.isActive) {
    return <></>;
  }

  const midweekServices = WEEKLY_SCHEDULE.filter((service) => service.dayOfWeek !== 0);

  // One icon-less schedule list, grouped by day - Sunday's label appears
  // once above its morning and evening entries, same as every midweek day
  // gets one label above its single entry.
  const scheduleGroups = [
    {
      key: 'sunday',
      label: weekdayMap(0, language),
      entries: [
        { name: morningServiceString(language), time: '10:00 AM' },
        { name: eveningServiceString(language), time: '6:00 PM' },
      ],
    },
    ...midweekServices.map((service) => ({
      key: service.dayOfWeek,
      label: weekdayMap(service.dayOfWeek, language),
      entries: [{ name: MIDWEEK_SHORT_NAMES[service.dayOfWeek](language), time: service.displayTime }],
    })),
  ];

  const exploreLinkStyle = {
    fontFamily: theme.typography.paragraphFontFamily,
    fontSize: '15px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    textDecoration: 'none',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 'var(--nav-height)',
        left: 0,
        width: '100%',
        zIndex: '100',
        backgroundColor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.brand.border}`,
        boxShadow: '0 16px 40px rgba(22, 50, 74, 0.15)',
        fontFamily: theme.typography.fontFamily,
      }}
    >
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '28px 48px 18px',
          boxSizing: 'border-box',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '36px',
        }}
      >
        {/* VISIT BETHEL */}
        <div style={{ flex: '1 1 240px', minWidth: '220px' }}>
          <Eyebrow theme={theme}>{secondaryVisitEyebrowString(language)}</Eyebrow>
          <SectionHeading theme={theme}>{secondaryVisitHeadingString(language)}</SectionHeading>

          <IconRow icon={<LocationOnIcon sx={{ fontSize: '20px' }} />} theme={theme}>
            <a
              href={ADDRESS_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'block', fontFamily: theme.typography.paragraphFontFamily, fontSize: '15px', color: theme.palette.text.primary, textDecoration: 'none' }}
            >
              {street}, <br />
              {city}, {state} {zip}
            </a>
          </IconRow>

          <div style={{ marginTop: '10px', paddingLeft: '54px' }}>
            <a
              href={ADDRESS_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: '14px',
                fontWeight: 600,
                color: theme.palette.primary.main,
                textDecoration: 'none',
              }}
            >
              {getDirectionsString(language)} →
            </a>
          </div>
        </div>

        {/* WEEKLY GATHERINGS */}
        <div style={{ flex: '1 1 260px', minWidth: '240px' }}>
          <Eyebrow theme={theme}>{secondaryGatheringsEyebrowString(language)}</Eyebrow>
          <SectionHeading theme={theme}>{secondaryGatheringsHeadingString(language)}</SectionHeading>

          {scheduleGroups.map((group, index) => (
            <div
              key={group.key}
              style={{
                padding: '10px 0',
                borderBottom: index < scheduleGroups.length - 1 ? `1px solid ${theme.palette.brand.border}` : 'none',
              }}
            >
              <div
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: theme.palette.text.secondary,
                  marginBottom: '2px',
                }}
              >
                {group.label}
              </div>
              {group.entries.map((entry) => (
                <div
                  key={entry.name}
                  style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}
                >
                  <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: '15px', fontWeight: 500, color: theme.palette.text.primary }}>
                    {entry.name}
                  </div>
                  <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: '13px', color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    {entry.time}
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div style={{ marginTop: '12px' }}>
            <FooterLink theme={theme} to={serviceTimesPath} onClick={props.handleClick}>
              {viewFullScheduleString(language)}
            </FooterLink>
          </div>
        </div>

        {/* EXPLORE */}
        <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
          <Eyebrow theme={theme}>{secondaryExploreEyebrowString(language)}</Eyebrow>
          <SectionHeading theme={theme}>{secondaryExploreHeadingString(language)}</SectionHeading>

          <div style={{ borderBottom: `1px solid ${theme.palette.brand.border}` }}>
            <NavLink to={announcementsPath} onClick={props.handleClick} style={{ textDecoration: 'none' }}>
              <IconRow icon={<CampaignIcon sx={{ fontSize: '20px' }} />} theme={theme}>
                <span style={exploreLinkStyle}>{announcementsString(language)}</span>
              </IconRow>
            </NavLink>
          </div>
          <div style={{ borderBottom: `1px solid ${theme.palette.brand.border}` }}>
            <NavLink to={eventsPath} onClick={props.handleClick} style={{ textDecoration: 'none' }}>
              <IconRow icon={<EventIcon sx={{ fontSize: '20px' }} />} theme={theme}>
                <span style={exploreLinkStyle}>{eventsPageHeadingString(language)}</span>
              </IconRow>
            </NavLink>
          </div>
          <div style={{ borderBottom: `1px solid ${theme.palette.brand.border}` }}>
            <NavLink to={bibleReadingPlanPath} onClick={props.handleClick} style={{ textDecoration: 'none' }}>
              <IconRow icon={<AutoStoriesIcon sx={{ fontSize: '20px' }} />} theme={theme}>
                <span style={exploreLinkStyle}>{bibleReadingPlanString(language)}</span>
              </IconRow>
            </NavLink>
          </div>
          <button
            type="button"
            onClick={() => setMinistriesOpen(!ministriesOpen)}
            style={{
              display: 'block',
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              cursor: 'pointer',
              textAlign: 'left',
              borderBottom: ministriesOpen ? 'none' : `1px solid ${theme.palette.brand.border}`,
            }}
          >
            <IconRow icon={<ChurchIcon sx={{ fontSize: '20px' }} />} theme={theme}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={exploreLinkStyle}>{ministriesString(language)}</span>
                <ExpandMoreIcon
                  sx={{
                    fontSize: '18px',
                    color: theme.palette.text.secondary,
                    transform: ministriesOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </div>
            </IconRow>
          </button>

          {ministriesOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px 0 0 54px' }}>
              <NavLink to={awanaPath} onClick={props.handleClick} style={exploreLinkStyle}>
                Awana
              </NavLink>
              <NavLink to={ezraPath} onClick={props.handleClick} style={exploreLinkStyle}>
                Ezra Conference
              </NavLink>
            </div>
          )}
        </div>

        {/* CONNECT - social icons use Olive Sage as a one-off accent */}
        <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
          <Eyebrow theme={theme}>{secondaryConnectEyebrowString(language)}</Eyebrow>
          <SectionHeading theme={theme}>{secondaryConnectHeadingString(language)}</SectionHeading>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '18px', fontSize: '19px', color: theme.palette.brand.sage }}>
            {[
              { href: facebookPath, icon: faFacebook, label: 'Facebook' },
              { href: instagramPath, icon: faInstagram, label: 'Instagram' },
              { href: twitterPath, icon: faXTwitter, label: 'Twitter' },
              { href: youtubePath, icon: faYoutube, label: 'YouTube' },
              { href: vimeoPath, icon: faVimeoV, label: 'Vimeo' },
            ].map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
                style={{ color: 'inherit', display: 'flex' }}
              >
                <FontAwesomeIcon icon={social.icon} size="xs" />
              </a>
            ))}
          </div>

          <NavLink
            to={mediaPath}
            onClick={props.handleClick}
            style={{ ...exploreLinkStyle, display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <PhotoLibraryIcon sx={{ fontSize: '18px', color: theme.palette.primary.dark }} />
            {mediaString(language)}
          </NavLink>
        </div>
      </div>

      <MenuFooterBar
        theme={theme}
        language={language}
        handleClick={props.handleClick}
        trailing={
          <LanguageSwitcher
            theme={theme}
            language={language}
            onChangeLanguage={(lang) => dispatch(updateLanguage(lang))}
            EN={EN}
            RO={RO}
          />
        }
        style={{
          borderTop: `1px solid ${theme.palette.brand.border}`,
          padding: '14px 48px',
          maxWidth: '1400px',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}
